/**
 * /deal/:id — the public, no-login deal card.
 *
 * What the "Copy public link" button on a listing points at. An advisor or
 * admin pastes it into a reply to an inbound inquiry; whoever opens it sees
 * the card (title, teaser, category, state, category KPIs) and is invited to
 * sign in for the full listing. The API decides what is safe to show
 * (app/services/public_deals.py) — this page renders exactly what it gets.
 */
import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowRight, BadgeCheck, Ban, Building2, Hash, MapPin } from "lucide-react";

import BrandLogo from "@/components/BrandLogo";
import { Button } from "@/components/ui/button";
import Loader from "@/components/ui/loader";
import { useAuth } from "@/contexts/AuthContext";
import { API_BASE_URL } from "@/lib/api";

interface PublicDealKpi {
  key: string;
  label: string;
  value: number | null;
  display: string;
}

interface PublicDeal {
  id: string;
  reference_code: string | null;
  title: string;
  description: string | null;
  category_name: string | null;
  category_slug: string | null;
  state_name: string | null;
  country_name: string | null;
  kpis: PublicDealKpi[];
  highlights: string[];
  image_url: string | null;
  published_at: string | null;
  url: string;
  share_url: string;
}

/** /d/:id in local dev, where there's no Vercel rewrite to the preview page. */
export function ShortDealRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/deal/${id}`} replace />;
}

export default function PublicDealPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const [imageFailed, setImageFailed] = useState(false);
  const [deal, setDeal] = useState<PublicDeal | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE_URL}/public/deals/${encodeURIComponent(id ?? "")}`)
      .then(async (res) => {
        if (!res.ok) throw new Error("This deal is no longer available.");
        return res.json() as Promise<PublicDeal>;
      })
      .then((data) => {
        if (cancelled) return;
        setDeal(data);
        document.title = `${data.title} | BusinessLocating`;
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message || "This deal is no longer available.");
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const place = deal ? [deal.state_name, deal.country_name].filter(Boolean).join(", ") : "";
  const listingPath = `/listings/${id}`;

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <header className="sticky top-0 z-40 border-b border-border/70 bg-card/90 backdrop-blur">
        <div className="mx-auto flex w-full max-w-6xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
          <Link to="/" className="flex items-center gap-2.5">
            <BrandLogo className="h-9 w-auto" />
          </Link>
          {!user && (
            <div className="flex items-center gap-2">
              <Button asChild variant="ghost" size="sm">
                <Link to={`/sign-in?next=${encodeURIComponent(listingPath)}`}>Sign in</Link>
              </Button>
              <Button asChild size="sm">
                <Link to="/register">Join free</Link>
              </Button>
            </div>
          )}
        </div>
      </header>

      <main className="flex flex-1 items-start justify-center px-4 py-10 sm:px-6 sm:py-16">
        {!deal && !error && <Loader className="min-h-[40vh]" />}

        {error && (
          <div className="w-full max-w-xl rounded-2xl border border-border/80 bg-card px-6 py-10 text-center shadow-card">
            <Ban className="mx-auto h-9 w-9 text-muted-foreground/60" />
            <h1 className="heading-bold mt-4 text-xl text-foreground">{error}</h1>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">
              It may have been sold or withdrawn. Browse the businesses that are for sale right now.
            </p>
            <Button asChild className="mt-6">
              <Link to="/register">
                See live deals
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </div>
        )}

        {deal && (
          <article className="w-full max-w-2xl overflow-hidden rounded-[1.6rem] border border-border/80 bg-card shadow-card">
            <div className="relative aspect-[1200/630] w-full bg-muted/50">
              {deal.image_url && !imageFailed ? (
                <img
                  src={deal.image_url}
                  alt=""
                  className="h-full w-full object-cover"
                  onError={() => setImageFailed(true)}
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-muted-foreground/40">
                  <Building2 className="h-12 w-12" />
                </div>
              )}
            </div>
            <div className="border-b border-border/70 px-6 py-6 sm:px-8">
              <div className="flex flex-wrap items-center gap-2">
                {deal.category_name && (
                  <span className="public-pill inline-flex">
                    <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                    {deal.category_name}
                  </span>
                )}
                {deal.reference_code && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-1 text-xs font-semibold text-muted-foreground">
                    <Hash className="h-3 w-3" />
                    {deal.reference_code}
                  </span>
                )}
              </div>
              <h1 className="mt-4 font-display text-2xl leading-tight text-foreground sm:text-3xl">
                {deal.title}
              </h1>
              {place && (
                <div className="mt-2 flex items-center gap-1.5 text-sm text-muted-foreground">
                  <MapPin className="h-4 w-4 shrink-0" />
                  {place}
                </div>
              )}
            </div>

            {deal.kpis.length > 0 && (
              <div className="grid grid-cols-2 gap-px bg-border/70 sm:grid-cols-4">
                {deal.kpis.map((kpi) => (
                  <div key={kpi.key} className="bg-card px-4 py-4 sm:px-5">
                    <div className="text-[0.66rem] font-bold uppercase tracking-[0.14em] text-muted-foreground">
                      {kpi.label}
                    </div>
                    <div className="mt-1.5 text-lg font-extrabold tracking-tight text-primary">
                      {kpi.display}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="px-6 py-6 sm:px-8">
              {deal.description && (
                <p className="whitespace-pre-line text-sm leading-7 text-muted-foreground sm:text-base">
                  {deal.description}
                </p>
              )}

              {deal.highlights.length > 0 && (
                <div className="mt-5 flex flex-wrap gap-2">
                  {deal.highlights.map((h) => (
                    <span
                      key={h}
                      className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs font-semibold text-primary"
                    >
                      <BadgeCheck className="h-3.5 w-3.5" />
                      {h}
                    </span>
                  ))}
                </div>
              )}

              {user ? (
                <div className="mt-7">
                  <Button asChild>
                    <Link to={listingPath}>
                      Open the full listing
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </Button>
                </div>
              ) : (
                <div className="mt-7 rounded-xl border border-primary/25 bg-primary/[0.05] p-5">
                  <h2 className="heading-bold text-base text-foreground">Want the full picture?</h2>
                  <p className="mt-1 text-sm leading-6 text-muted-foreground">
                    The full financials, location details and the advisor handling this deal are
                    available to BusinessLocating members. Joining is free.
                  </p>
                  <div className="mt-4 flex flex-col gap-2.5 sm:flex-row">
                    <Button asChild>
                      <Link to="/register">
                        Create a free account
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    </Button>
                    <Button asChild variant="outline">
                      <Link to={`/sign-in?next=${encodeURIComponent(listingPath)}`}>
                        I have an account
                      </Link>
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </article>
        )}
      </main>

      <footer className="border-t border-border/70 bg-card/60 px-4 py-6 sm:px-6">
        <p className="mx-auto max-w-6xl text-center text-xs text-muted-foreground">
          &copy; {new Date().getFullYear()} BusinessLocating Hub &mdash; We Find the Perfect Fit.
        </p>
      </footer>
    </div>
  );
}
